import { t } from "@lingui/macro";
import {
  ExpenseInvoiceStatusEnum,
  type ExpenseItemsListItemFragment,
  ExpenseItemsListingDocument,
  ExpenseItemsListingFilterOptionsDocument,
  type ExpenseItemsListingFilterOptionsQuery,
  type ExpenseItemsListingFilterOptionsQueryVariables,
  type ExpenseItemsListingQuery,
  type ExpenseItemsListingQueryVariables,
  ExpenseItemsListingSummaryDocument,
  type ExpenseItemsListingSummaryQuery,
  type ExpenseItemsListingSummaryQueryVariables,
  type ExpenseItemsTimeBillingOrderByColumn,
  ExpenseItemsTimeBillingWhereColumn,
  ExpenseStatusEnum,
  ExpenseTypeEnum,
  OutgoingInvoiceItemsListingBudgetItemFilterOptionsDocument,
  type OutgoingInvoiceItemsListingBudgetItemFilterOptionsQuery,
  type OutgoingInvoiceItemsListingBudgetItemFilterOptionsQueryVariables,
  type SortOrder,
  SqlOperator,
  type TimeBillingSummary,
} from "@src/__generated__/graphql";
import { BankStore } from "@src/components/modules/bank/stores";
import { QuickFilters } from "@src/components/modules/expenses/listing/StoreExpenses";
import type { BodyRow } from "@src/components/ui-kit";
import { BulkActionsStore } from "@src/components/widgets/bulk-actions/store";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { type AppStore, appStore } from "@src/stores/AppStore";
import { commonSearchParams } from "@src/utils/apolloHelpers";
import {
  getBooleanFilterOptions,
  getBudgetItemTypeFilterOptions,
  getPaymentStatusFilterOptions,
} from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { contractorOptionsQuery } from "@src/utils/components/filters/paginatedOptions";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import mapToOptions from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import {
  createActiveQueryParam,
  getQueryParamOptional,
} from "@src/utils/routing";
import { camelCase, lowerCase, map, upperFirst } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import type { Query } from "nextjs-routes";

export interface ExpenseItemsTableItem
  extends BodyRow,
    ExpenseItemsListItemFragment {}

export class StoreExpenseItems {
  readonly tableKey = "expense-items-listing";

  pagination = new PaginationState(this.tableKey, {
    onChangePagination: () => {
      this.fetchItems();
    },
  });

  orderBy = new OrderBy<ExpenseItemsTimeBillingOrderByColumn>();

  bulkActions = new BulkActionsStore([]);

  where = new Filters<ExpenseItemsTimeBillingWhereColumn>(
    [
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.AmountFrom,
        operator: SqlOperator.Gte,
        title: t`Amount from`,
        options: [],
        mutateToGql: BankStore.mutateAmountFilter,
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.AmountTo,
        operator: SqlOperator.Lte,
        title: t`Amount to`,
        options: [],
        mutateToGql: BankStore.mutateAmountFilter,
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.ContractorId,
        isPaginated: true,
        optionsQuery: contractorOptionsQuery,
        operator: SqlOperator.In,
        title: t`Contractor`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.ProjectId,
        operator: SqlOperator.In,
        title: t`Project`,
        options: [],
        onChange: (value) => this.handleProjectFilterChange(value),
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.MandatoryId,
        operator: SqlOperator.In,
        title: t`Responsible`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.WorkspaceCompanyId,
        operator: SqlOperator.In,
        title: t`Legal entity`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.DateOfSupply,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Date of supply`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.DueDate,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Due date`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.PaidAt,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Paid at`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.CreatedAt,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Created at`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.Status,
        operator: SqlOperator.In,
        title: t`Status`,
        options: map(ExpenseStatusEnum, (status) => ({
          label: upperFirst(lowerCase(status)),
          value: status,
        })),
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.InvoiceStatus,
        operator: SqlOperator.In,
        title: t`Invoice status`,
        options: map(ExpenseInvoiceStatusEnum, (f) => ({
          label: upperFirst(lowerCase(f)),
          value: camelCase(f),
        })),
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        title: t`Payment status`,
        options: getPaymentStatusFilterOptions(),
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.BudgetItemId,
        operator: SqlOperator.In,
        title: t`Budget item`,
        noOptionsMessage: t`Please select Project first`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.BudgetItemType,
        operator: SqlOperator.In,
        title: t`Budget item type`,
        options: getBudgetItemTypeFilterOptions(),
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.TagId,
        operator: SqlOperator.In,
        title: t`Tag`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.AccountingAccountId,
        operator: SqlOperator.In,
        title: t`Accounting account`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.AccountingCentreId,
        operator: SqlOperator.In,
        title: t`Cost center`,
        options: [],
      }),
      new Filter({
        column: ExpenseItemsTimeBillingWhereColumn.Type,
        operator: SqlOperator.In,
        title: t`Type`,
        options: map(ExpenseTypeEnum, (type) => {
          if (type === ExpenseTypeEnum.Card)
            return {
              label: t`Card payments`,
              value: type.toLowerCase(),
            };
          return {
            label: upperFirst(lowerCase(type)),
            value: type.toLowerCase(),
          };
        }),
      }),
      new Filter({
        hidden: !appStore.workspaceStore.hasModuleForPage("time_billing"),
        column: ExpenseItemsTimeBillingWhereColumn.HasTimeBilling,
        title: t`Has time billing`,
        operator: SqlOperator.Eq,
        options: getBooleanFilterOptions(),
        value: [],
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
        this.fetchSummary();
        this.selectedTotalAmount = 0;
      },
      tableKey: this.tableKey,
      orderBy: this.orderBy,
    },
  );

  @observable.ref
  tableData: ExpenseItemsTableItem[] = [];

  @observable.ref
  searchTerm = "";

  @observable.ref
  selectedTotalAmount = 0;

  @observable
  brandsMap: Map<string, string> = new Map();

  @observable
  usersMap: Map<string, string> = new Map();

  @observable
  accountingCentresMap: Map<string, string> = new Map();

  private itemsFetcher = new FetchHelper<
    ExpenseItemsListingQuery,
    ExpenseItemsListingQueryVariables
  >(ExpenseItemsListingDocument);

  private filterOptionsFetcher = new FetchHelper<
    ExpenseItemsListingFilterOptionsQuery,
    ExpenseItemsListingFilterOptionsQueryVariables
  >(ExpenseItemsListingFilterOptionsDocument);

  private budgetItemFilterOptionsFetcher = new FetchHelper<
    OutgoingInvoiceItemsListingBudgetItemFilterOptionsQuery,
    OutgoingInvoiceItemsListingBudgetItemFilterOptionsQueryVariables
  >(OutgoingInvoiceItemsListingBudgetItemFilterOptionsDocument);

  @observable.ref
  summary: TimeBillingSummary | null = null;

  private summaryFetcher = new FetchHelper<
    ExpenseItemsListingSummaryQuery,
    ExpenseItemsListingSummaryQueryVariables
  >(ExpenseItemsListingSummaryDocument);

  constructor(
    private appStore: AppStore,
    query: Query,
  ) {
    makeObservable(this);
    this.setFiltersFromQuery(query);
    this.fetchFilterOptions();
    this.fetchSummary();
    this.fetchItems();
  }

  @computed
  get isLoading(): boolean {
    return this.itemsFetcher.isLoading.value;
  }

  @computed
  get isSummaryLoading(): boolean {
    return this.summaryFetcher.isLoading.value;
  }

  @computed
  get searchParams() {
    return {
      ...commonSearchParams(this),
      ...createActiveQueryParam(QuickFilters.ExpenseItems),
    };
  }

  @action.bound
  setSearchTerm(value: string): void {
    if (this.searchTerm === value) return;
    this.searchTerm = value;
    this.fetchSummary();
    this.pagination.resetPage();
  }

  @action.bound
  handleSelectionChange(ids: string[]): void {
    if (ids.length === 0) {
      this.selectedTotalAmount = 0;
    }

    const nextSelected = new Set(ids);
    const previousSelected = this.bulkActions.selectedIds;

    for (const id of nextSelected) {
      if (previousSelected.has(id)) continue;
      const item = this.tableData.find((item) => item.id === id);
      if (!item) continue;
      this.selectedTotalAmount += item.amount;
    }

    for (const id of previousSelected) {
      if (nextSelected.has(id)) continue;
      const item = this.tableData.find((item) => item.id === id);
      if (!item) continue;
      this.selectedTotalAmount -= item.amount;
    }

    this.bulkActions.setSelectedIds(ids);

    if (this.bulkActions.isSelectAllChecked) {
      this.selectedTotalAmount = this.summary?.amount ?? 0;
    }

    if (nextSelected.size === 0) {
      this.selectedTotalAmount = 0;
    }
  }

  @action.bound
  handleOrderChange(orderBy: {
    column: ExpenseItemsTimeBillingOrderByColumn;
    order: SortOrder | undefined;
  }): void {
    if (!orderBy.order) {
      this.orderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }

    this.orderBy.setClauses([{ column: orderBy.column, order: orderBy.order }]);
    this.pagination.resetPage();
  }

  @action.bound
  private async fetchFilterOptions(): Promise<void> {
    this.appStore.tagStore.fetchTags();
    const [data, error] = await this.filterOptionsFetcher.fetch();

    if (error || !data) return;

    data.clientsSimpleMap.forEach((client) => {
      client.brands.forEach((brand) => {
        this.brandsMap.set(brand.id, mapToOptions.brands([brand])[0].label);
      });
    });

    data.userSimpleMap.forEach((user) => {
      this.usersMap.set(user.id, user.full_name);
    });

    data.accountingCentres.forEach((centre) => {
      this.accountingCentresMap.set(centre.id, centre.title);
    });

    this.where.filtersByColumn
      .get(ExpenseItemsTimeBillingWhereColumn.BrandId)
      ?.setOptions(mapToOptions.brandsSectioned(data.clientsSimpleMap));

    this.where.filtersByColumn
      .get(ExpenseItemsTimeBillingWhereColumn.ProjectId)
      ?.setOptions(mapToOptions.projects(data.projectsSimpleMap));

    this.where.filtersByColumn
      .get(ExpenseItemsTimeBillingWhereColumn.MandatoryId)
      ?.setOptions(
        data.userSimpleMap.map(({ id, full_name }) => ({
          value: id,
          label: full_name,
        })),
      );

    this.where.filtersByColumn
      .get(ExpenseItemsTimeBillingWhereColumn.AccountingCentreId)
      ?.setOptions(mapToOptions.toOptions(data.accountingCentres, "title"));

    this.where.filtersByColumn
      .get(ExpenseItemsTimeBillingWhereColumn.AccountingAccountId)
      ?.setOptions(
        mapToOptions.accountingAccounts(
          this.appStore.workspaceStore.accountingAccounts,
        ),
      );

    this.where.filtersByColumn
      .get(ExpenseItemsTimeBillingWhereColumn.TagId)
      ?.setOptions(
        mapToOptions.toOptions(this.appStore.tagStore.tags, "title"),
      );

    const workspaceCompaniesFilter = this.where.filtersByColumn.get(
      ExpenseItemsTimeBillingWhereColumn.WorkspaceCompanyId,
    );

    workspaceCompaniesFilter?.setOptions(
      mapToOptions.workspaceCompanies(this.appStore.workspaceStore.companies),
    );

    if (this.appStore.workspaceStore.companies.length > 1) return;
    workspaceCompaniesFilter?.hide();
  }

  @action.bound
  private async fetchItems(): Promise<void> {
    const [data, error] = await this.itemsFetcher.fetch({
      filters: {
        where: this.where.asGraphQueryParam,
        orderBy: this.orderBy.asWhereParam,
        search: this.searchTerm,
      },
      first: this.pagination.asParams.first,
      page: this.pagination.asParams.page,
    });

    if (error || !data.expenseItemsForTimeBilling) return;

    this.tableData = data.expenseItemsForTimeBilling.data;

    this.pagination.setFromPaginatorInfo(
      data.expenseItemsForTimeBilling.paginatorInfo,
    );
  }

  @action.bound
  private async fetchSummary(): Promise<void> {
    const [data, error] = await this.summaryFetcher.fetch({
      filters: {
        where: this.where.asGraphQueryParam,
        search: this.searchTerm,
      },
    });

    if (error || !data?.expenseItemsForTimeBillingSummary) return;

    this.summary = data.expenseItemsForTimeBillingSummary;
    this.bulkActions.setAllIds(data.expenseItemsForTimeBillingSummary.allIds);
  }

  @action.bound
  private setFiltersFromQuery(query: Query): void {
    this.searchTerm = getQueryParamOptional("search", query) ?? "";
    this.where.fromURLSearchParam(query, { skipOnChange: true });
    this.orderBy.fromURLSearchParam(query);
    this.pagination.fromURLSearchParam(query);
  }

  @action.bound
  private async handleProjectFilterChange(
    value: string[] | undefined,
  ): Promise<void> {
    const budgetItemFilter = this.where.filtersByColumn.get(
      ExpenseItemsTimeBillingWhereColumn.BudgetItemId,
    );

    if (!value || value?.length === 0) {
      budgetItemFilter?.clear();
      budgetItemFilter?.setOptions([]);
      budgetItemFilter?.setNoOptionsMessage(t`Please select Project first`);
      return;
    }

    budgetItemFilter?.setNoOptionsMessage(t`No options`);
    const [data, error] = await this.budgetItemFilterOptionsFetcher.fetch({
      projectId: value[0],
    });

    if (error || !data) {
      budgetItemFilter?.setOptions([]);
      return;
    }

    budgetItemFilter?.setOptions(
      mapToOptions.budgetItems(
        data.ourWorkBudgetItemsSimpleMap.concat(
          // @ts-expect-error - Typename mismatch but otherwise they are the same
          data.expenseBudgetItemsSimpleMap,
        ),
      ),
    );
  }
}
